import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { IconButton } from '@material-ui/core';
import { Clear, DeleteOutline } from '@material-ui/icons';
import {
  ceil, chunk, compose, map, nth, size, toNumber,
} from 'lodash/fp';
import dayjs from 'dayjs';
import {
  Dialog, DialogTitle, DialogContent, Pagination,
} from '../../../common/components';
import { readableHiddenContent } from '../../../common/styles';

const cols = [
  { id: 'title', styles: css`width: 52%;` },
  { id: 'writer', styles: css`width: 22%;` },
  { id: 'saveAt', styles: css`width: 22%;` },
  { id: 'delete', styles: css`width: 4%;` },
];

const S = {
  Dialog: styled(Dialog)`
    .paper {
      position: relative;
      width: 600px;
    }
  `,
  DialogTitle: styled(DialogTitle)`
    margin-bottom: 24px;
  `,
  Heading: styled.h3`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(20)};
    font-weight: 500;
    line-height: 1.5;
  `,
  ClearButton: styled(IconButton)`
    position: absolute;
    top: 2rem;
    right: 2.5rem;
  `,
  Clear: styled(Clear)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 32px;
  `,
  DialogContent: styled(DialogContent)`
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  Info: styled.p`
    width: 100%;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
  `,
  Table: styled.table`
    margin-bottom: 24px;
    border-collapse: collapse;
    width: 100%;
  `,
  Col: styled.col`
    ${({ styles }) => styles};
  `,
  TableHead: styled.thead`
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
    color: #424242;
  `,
  TableBody: styled.tbody`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    line-height: 1.54;
  `,
  TableRow: styled.tr`
    height: 40px;
    thead & {
      height: 32px;
      border-top: 1px solid ${({ theme }) => theme.palette.grey[400]};
      border-bottom: 1px solid ${({ theme }) => theme.palette.grey[400]};
    }
    tbody & {
      cursor: pointer;
    }
    tbody &:hover {
      background-color: ${({ theme }) => theme.palette.hexToRgb(theme.palette.blueGrey[50], 0.5)};
    }
    &:hover .MuiIconButton-root {
      opacity: 1;
    }
  `,
  TableCell: styled.td`
    font-weight: 500;
    text-align: left;
    :first-child {
      font-size: ${({ theme }) => theme.typography.pxToRem(15)};
      line-height: 1.53;
    }
    :last-child {
      cursor: auto;
    }
    thead &:first-child {
      font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    }
  `,
  Ellipsis: styled.div`
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  HiddenContent: styled.div`
    ${readableHiddenContent}
  `,
  DeleteButton: styled(IconButton)`
    opacity: 0;
    padding: 2px;
    :focus,
    :hover {
      opacity: 1;
    }
  `,
  DeleteOutline: styled(DeleteOutline)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.5rem;
  `,
};

const OutboxDialog = ({
  drafts, match, open, setSaved, onClick, onClose, onDelete, onEntered,
}) => {
  const [pageNumber, setPageNumber] = useState(1);

  const rows = useMemo(() => compose(
    nth(pageNumber - 1),
    chunk(10),
  )(drafts.data), [drafts, pageNumber]);
  const pageCount = useMemo(() => (ceil(size(drafts.data) / 10) || 1), [drafts.data]);

  const moveToPage = useCallback(event => setPageNumber(toNumber(event.currentTarget.dataset.pageNumber)), []);
  const deleteDraft = useCallback(event => onDelete({
    did: event.currentTarget.dataset.did,
    sid: match.params.sid,
  }), [match.params.sid, onDelete]);
  const handleClick = useCallback((event) => {
    onClick({ did: event.currentTarget.dataset.did, sid: match.params.sid });

    setSaved(true);

    onClose();
  }, [match.params.sid, onClick, onClose, setSaved]);

  return (
    <S.Dialog open={open} onClose={onClose} onEntered={onEntered}>
      <S.DialogTitle>
        <S.Heading>
          임시보관함
        </S.Heading>
      </S.DialogTitle>
      <S.ClearButton type="button" size="small" onClick={onClose}>
        <S.Clear />
      </S.ClearButton>
      <S.DialogContent>
        <S.Info>{`총 ${size(drafts.data)}개`}</S.Info>
        <S.Table>
          <colgroup>
            {map(col => <S.Col key={col.id} styles={col.styles} />, cols)}
          </colgroup>
          <S.TableHead>
            <S.TableRow>
              <S.TableCell as="th">
                제목
              </S.TableCell>
              <S.TableCell as="th">
                작성자
              </S.TableCell>
              <S.TableCell as="th">
                수정시간
              </S.TableCell>
              <S.TableCell as="th">
                <S.HiddenContent>
                  삭제
                </S.HiddenContent>
              </S.TableCell>
            </S.TableRow>
          </S.TableHead>
          <S.TableBody>
            {map(draft => (
              <S.TableRow
                key={draft.id}
                data-did={draft.id}
                onClick={handleClick}
              >
                <S.TableCell>
                  <S.Ellipsis>
                    {draft.title || '제목 없음'}
                  </S.Ellipsis>
                </S.TableCell>
                <S.TableCell>
                  <S.Ellipsis>
                    {draft.writer}
                  </S.Ellipsis>
                </S.TableCell>
                <S.TableCell>
                  <S.Ellipsis>
                    {dayjs(draft.updatedAt.toDate()).format('YYYY.MM.DD. HH:mm')}
                  </S.Ellipsis>
                </S.TableCell>
                <S.TableCell onClick={event => event.stopPropagation()}>
                  <S.DeleteButton
                    type="button"
                    size="small"
                    data-did={draft.id}
                    onClick={deleteDraft}
                  >
                    <S.DeleteOutline />
                  </S.DeleteButton>
                </S.TableCell>
              </S.TableRow>
            ), rows)}
          </S.TableBody>
        </S.Table>
        <Pagination
          pageCount={pageCount}
          pageNumber={pageNumber}
          onClick={moveToPage}
        />
      </S.DialogContent>
    </S.Dialog>
  );
};

OutboxDialog.propTypes = {
  drafts: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({})),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.instanceOf(Error),
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      sid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  setSaved: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEntered: PropTypes.func.isRequired,
};

OutboxDialog.defaultProps = {
  drafts: {
    data: null,
    loading: false,
    error: null,
  },
};

export default withRouter(OutboxDialog);
